/**
 * Typen des Listen-Standards (AP-25-B).
 *
 * Filterleiste und Spalten-Konfiguration sind laut
 * `docs/pattern-auswertungsmaske.md` der verbindliche Standard JEDER
 * Listenseite. Bis zum 9.9.2026 lagen diese Typen in
 * `features/time-tracking/types/time-tracking.types.ts` — jede Listenseite
 * ausserhalb der Zeiterfassung musste dafuer die Modulgrenze verletzen. Fuer
 * einen zweiten Entwickler war die eigene Konvention damit unerfuellbar:
 * Grenzverstoss oder Kopie, etwas Drittes gab es nicht.
 *
 * Hier steht nur, was jede Liste braucht. Fachliche Zeiterfassungs-Typen
 * bleiben, wo sie hingehoeren.
 */

// ─── ColumnBar Types ──────────────────────────────────────────────────────────

/** Categories of table columns */
export type ColumnFieldCategory = 'core' | 'project' | 'time' | 'absence' | 'customField' | 'lohnverrechnung' | 'price';

/** Definition of a configurable table column */
export interface ColumnFieldDef {
  /** Unique column key */
  key: string;
  /** Header label */
  label: string;
  /** Category for grouping in the "add column" dropdown */
  category: ColumnFieldCategory;
  /** Whether to show a sum in the totals row (only for numeric/duration columns) */
  summable?: boolean;
  /** Original field type from CustomFieldSchema (e.g. 'checkbox', 'number', 'text') */
  fieldType?: string;
  /** Text alignment in cells */
  align?: 'left' | 'right';
  /**
   * AP-30-F — Das **Feldrecht**, ohne das diese Spalte nicht existiert.
   *
   * Sie ersetzt `requiresPricePermission`, das an der Mandanten- bzw.
   * Benutzereinstellung `showPriceFields` hing. Damit gab es zwei Wahrheiten
   * über dieselbe Spalte, und welche galt, hing davon ab, wer zuletzt gefragt
   * hatte. Jetzt gilt: **eine Wahrheit, und sie heißt `time.price.view`.**
   *
   * **Fehlt das Recht, verschwindet die Spalte aus fünf Dingen auf einmal**:
   * Anzeige, Spaltenauswahl, Sortierung, Filter und Summenzeile. Eine
   * Summenzeile über Einzelpreise verrät die Einzelpreise, sobald man filtern
   * kann; eine Sortierung nach einem unsichtbaren Feld ebenso.
   *
   * Und das Entscheidende: **Das hier ist Kosmetik.** Die eigentliche Sperre ist
   * die Beschneidung der Antwort im Backend — wer ein Feld nur ausblendet, hat
   * es weiterhin im Netzwerk-Tab.
   */
  requiresPermission?: string;
}

/**
 * Persisted column layout for a user (stored in localStorage).
 * Ordered list of column keys shown in the table.
 */
export interface ColumnSortConfig {
  key: string;
  dir: 'asc' | 'desc';
}

export interface ColumnLayout {
  /** Ordered column keys (fixed "date" first + "actions" last are excluded from this list) */
  columnKeys: string[];
  /** Per-column pixel widths (optional; undefined = auto) */
  columnWidths?: Record<string, number>;
  /** Active sort column and direction */
  sortConfig?: ColumnSortConfig | null;
}

// ─── FilterBar Types ──────────────────────────────────────────────────────────

/** Operator for a filter field */
export type FilterOperator = 'is' | 'is_not' | 'contains' | 'not_contains' | 'gt' | 'lt';

/** Categories of filter fields – used for grouping in the "Add field" dropdown */
export type FilterFieldCategory = 'core' | 'project' | 'customField' | 'lohnverrechnung';

/** Definition of a filterable field */
export interface FilterFieldDef {
  /** Unique key – prefixed: "date_from", "status", "cf_kostenstelle", "sf_projektposition", "lv_schicht" */
  key: string;
  /** Display label */
  label: string;
  /** Input type for rendering */
  type: 'date' | 'datetime' | 'select' | 'select-multi' | 'text' | 'boolean' | 'number';
  /** Group shown in "Add field" dropdown */
  category: FilterFieldCategory;
  /** Available options for select fields – always value/label pairs */
  options?: { value: string; label: string }[];
  /** Applicable operators – determined by type */
  operators: FilterOperator[];
  /** For datetime fields: default time portion (HH:MM) applied when the user first sets a value, e.g. "00:00" or "23:59" */
  defaultTime?: string;
  /**
   * AP-30-F — Das Feldrecht, ohne das dieses Filterfeld nicht existiert.
   *
   * Ohne diese Zeile bliebe die Spalte verborgen und wäre trotzdem **filterbar**
   * — und über eine Reihe von Filtern erfährt man einen Einzelwert so genau wie
   * beim Hinsehen.
   */
  requiresPermission?: string;
}

/** One active filter with its current operator and value */
export interface ActiveFilter {
  fieldKey: string;
  operator: FilterOperator;
  value: string;
}

/**
 * Persisted filter-bar layout for a user (stored in localStorage).
 * Contains the ordered list of displayed field keys and the last used filter values.
 */
export interface SummaryFilterLayout {
  /** Ordered field keys shown in the filter bar */
  fieldKeys: string[];
  /** Last-used values per field */
  lastValues: Record<string, ActiveFilter>;
}

// ─── Feldsicht (AP-30-F) ──────────────────────────────────────────────────────

/**
 * Streicht jede Spalte und jedes Filterfeld, dessen Feldrecht fehlt.
 *
 * **Eine Funktion für beides und für alle Listen.** Der Grund steht in AP-30,
 * Abschnitt 6: Ein verborgenes Feld darf auch nicht sortierbar, filterbar oder
 * summierbar sein — und das ist nur zu halten, wenn es *eine* Stelle gibt, an
 * der die Liste entsteht. Fünf Aufrufer mit fünf Bedingungen wären fünf
 * Gelegenheiten, eine davon zu vergessen.
 */
export function ohneFehlendeRechte<T extends { requiresPermission?: string }>(
  felder: readonly T[],
  darf: (permission: string) => boolean,
): T[] {
  return felder.filter((f) => !f.requiresPermission || darf(f.requiresPermission));
}
